import { RegexPatterns } from './../../../../../../libs/shared/src/constants/regex-patterns';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { TranslocoModule } from '@jsverse/transloco';
import { ForgotPasswordStore } from './forgot-password.store';
import { LoadingStatus } from '../../shared/enums/loading';

@Component({
  selector: 'anihis-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatError,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatToolbarModule,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ForgotPasswordStore],
})
export class ForgotPasswordComponent extends FormBaseComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  store = inject(ForgotPasswordStore);

  readonly status = LoadingStatus;
  showValidationErrors = signal(false);

  readonly portalAccountTypes = [
    { value: 'ClinicOwner', labelKey: 'Clinic owner account' },
    { value: 'Clinic', labelKey: 'Clinic account' },
    { value: 'Manager', labelKey: 'Manager account' },
  ] as const;

  constructor() {
    super();
    this.initializeForm();
    this.route.queryParams.subscribe((params) => {
      const accountType = this.resolveAccountType(params['role'] ?? '');
      this.form.patchValue({ accountType }, { emitEvent: false });
    });
  }

  private resolveAccountType(role: string): 'ClinicOwner' | 'Clinic' | 'Manager' {
    const normalized = role.trim().toLowerCase();
    if (normalized === 'clinic') return 'Clinic';
    if (normalized === 'manager') return 'Manager';
    return 'ClinicOwner';
  }

  private initializeForm() {
    this.form = this.fb.group({
      accountType: [
        'ClinicOwner' as 'ClinicOwner' | 'Clinic' | 'Manager',
        Validators.required,
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(RegexPatterns.EMAIL_REGEX),
        ],
      ],
    });
  }

  back() {
    this.router.navigateByUrl('/');
  }

  backToLogin() {
    this.store.reset();
    this.router.navigateByUrl('/login');
  }

  resend() {
    const email = this.form.get('email')?.value;
    if (!email) {
      this.store.reset();
      return;
    }
    this.store
      .sendResetLink(email, this.form.get('accountType')?.value)
      .subscribe();
  }

  submit() {
    this.showValidationErrors.set(true);

    if (this.checkFormValidity()) return;

    const email = this.form.get('email')?.value;
    const accountType = this.form.get('accountType')?.value;
    this.store.sendResetLink(email, accountType).subscribe();
  }
}
